import type {Action} from 'platform/components';
import {Heading, HStack, Show, Spinner, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import type {PriceBasis} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, suffixTestId, type TestIdProps} from 'shared/web';

import {BasisPrice} from '../BasisPrice/BasisPrice';
import {ConfiguratorActionBar} from '../ConfiguratorActionBar/ConfiguratorActionBar';

export type ConfiguratorPriceBarProps = TestIdProps & {
  net?: string;
  gross?: string;
  basis?: PriceBasis;
  currency: string;
  isPending: boolean;
  /** Short reason why the user cannot continue yet. */
  hint?: string;
  leadingActions?: Action[];
  trailingActions?: Action[];
};

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

export function ConfiguratorPriceBar(props: ConfiguratorPriceBarProps) {
  const price =
    isNotNil(props.net) && isNotNil(props.gross) && isNotNil(props.basis)
      ? {net: props.net, gross: props.gross, basis: props.basis}
      : undefined;

  return (
    <ConfiguratorActionBar
      leading={
        <VStack spacing={0}>
          <Text size="xxSmall" color="secondary" alternative>
            {t('entity.newVehicle.labels.asConfigured', 'As configured').toUpperCase()}
          </Text>
          {match({price, isPending: isNil(props.basis) || props.isPending})
            .with({price: Pattern.nonNullable}, ({price: amounts}) => (
              <HStack align="center" spacing={2}>
                <BasisPrice
                  {...amounts}
                  currency={props.currency}
                  headingSize={2}
                  color="link"
                  noWrap
                />
                <Show when={props.isPending}>
                  <Spinner size="small" />
                </Show>
              </HStack>
            ))
            .with({isPending: true}, () => (
              <HStack align="center" spacing={2}>
                <Spinner size="small" />
                <Text size="small" color="secondary">
                  {t('entity.newVehicle.labels.updatingPrice', 'Updating price…')}
                </Text>
              </HStack>
            ))
            .otherwise(() => (
              <Heading size={2} color="tertiary">
                {EMPTY_PLACEHOLDER}
              </Heading>
            ))}
        </VStack>
      }
      hint={props.hint}
      leadingActions={props.leadingActions}
      trailingActions={props.trailingActions}
      data-testid={suffixTestId('priceBar', props)}
    />
  );
}
